import { Injectable } from '@angular/core';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { DeviceInfo, LocationData } from '../../interfaces/certificate.interface';

interface IpGeoData {
  ip: string;
  latitude: number | null;
  longitude: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceInfoService {
  private fingerprintPromise: Promise<string> | null = null;
  private ipGeoPromise: Promise<IpGeoData> | null = null;

  /**
   * Collect comprehensive device information
   */
  async collectDeviceInfo(): Promise<DeviceInfo> {
    const [fingerprint, ipGeoData] = await Promise.all([
      this.getDeviceFingerprint(),
      this.getIpAndLocation(),
    ]);

    const osInfo = this.getOSInfo();

    return {
      platform: this.detectPlatform(),
      version: osInfo.version,
      model: this.getDeviceModel(),
      app_version: this.getAppVersion(),
      device_id: fingerprint,
      manufacturer: this.getManufacturer(),
      os_version: osInfo.fullVersion,
      fingerprint: fingerprint,
      client_ip: ipGeoData.ip,
    };
  }

  /**
   * Get approximate location derived from IP address (no browser prompt)
   * Uses IP geolocation service to derive location from public IP
   */
  async getApproximateLocation(): Promise<LocationData | null> {
    const ipGeoData = await this.getIpAndLocation();

    if (ipGeoData.latitude !== null && ipGeoData.longitude !== null) {
      return {
        latitude: ipGeoData.latitude,
        longitude: ipGeoData.longitude,
      };
    }

    return null;
  }

  /**
   * Get or create cached device fingerprint using FingerprintJS
   */
  private async getDeviceFingerprint(): Promise<string> {
    if (!this.fingerprintPromise) {
      this.fingerprintPromise = this.generateFingerprint();
    }
    return this.fingerprintPromise;
  }

  /**
   * Generate device fingerprint using FingerprintJS
   */
  private async generateFingerprint(): Promise<string> {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      return result.visitorId;
    } catch (error) {
      console.warn('Failed to generate fingerprint:', error);
      return this.generateFallbackFingerprint();
    }
  }

  /**
   * Fallback fingerprint generation if FingerprintJS fails
   */
  private generateFallbackFingerprint(): string {
    const nav = navigator;
    const screen = window.screen;
    const components = [
      nav.userAgent || '',
      nav.language || '',
      screen.colorDepth?.toString() || '',
      screen.width?.toString() || '',
      screen.height?.toString() || '',
      new Date().getTimezoneOffset().toString(),
    ];
    return btoa(components.join('|'));
  }

  /**
   * Resolve and cache IP address with geolocation data
   * Uses ip-api.com which provides both IP and approximate location
   * No browser permission required - derives location from IP
   */
  private async getIpAndLocation(): Promise<IpGeoData> {
    if (this.ipGeoPromise) {
      return this.ipGeoPromise;
    }

    this.ipGeoPromise = this.fetchIpGeoData();
    return this.ipGeoPromise;
  }

  /**
   * Fetch IP and geolocation data from ip-api.com
   * Falls back to ipify if ip-api.com fails
   */
  private async fetchIpGeoData(): Promise<IpGeoData> {
    // Primary: ip-api.com (provides IP + location)
    try {
      const response = await fetch(
        'http://ip-api.com/json/?fields=query,lat,lon',
        { cache: 'no-store' },
      );

      if (response.ok) {
        const data = (await response.json()) as {
          query?: string;
          lat?: number;
          lon?: number;
        };

        if (data.query) {
          return {
            ip: data.query,
            latitude: data.lat ?? null,
            longitude: data.lon ?? null,
          };
        }
      }
    } catch (error) {
      console.warn('ip-api.com failed, trying fallback:', error);
    }

    // Fallback: ipapi.co (HTTPS, provides IP + location)
    try {
      const response = await fetch('https://ipapi.co/json/', {
        cache: 'no-store',
      });

      if (response.ok) {
        const data = (await response.json()) as {
          ip?: string;
          latitude?: number;
          longitude?: number;
        };

        if (data.ip) {
          return {
            ip: data.ip,
            latitude: data.latitude ?? null,
            longitude: data.longitude ?? null,
          };
        }
      }
    } catch (error) {
      console.warn('ipapi.co failed, trying ipify:', error);
    }

    // Final fallback: ipify (IP only, no location)
    try {
      const response = await fetch('https://api.ipify.org?format=json', {
        cache: 'no-store',
      });

      if (response.ok) {
        const data = (await response.json()) as { ip?: string };
        return {
          ip: data?.ip || 'unknown',
          latitude: null,
          longitude: null,
        };
      }
    } catch (error) {
      console.warn('All IP services failed:', error);
    }

    return {
      ip: 'unknown',
      latitude: null,
      longitude: null,
    };
  }

  /**
   * Detect the platform (Web, iOS, Android, etc.)
   */
  private detectPlatform(): string {
    const userAgent = navigator.userAgent.toLowerCase();

    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'iOS';
    }
    if (/android/.test(userAgent)) {
      return 'Android';
    }
    if (/macintosh|mac os x/.test(userAgent)) {
      return 'macOS';
    }
    if (/windows/.test(userAgent)) {
      return 'Windows';
    }
    if (/linux/.test(userAgent)) {
      return 'Linux';
    }

    return 'Web';
  }

  /**
   * Get OS information
   */
  private getOSInfo(): { name: string; version: string; fullVersion: string } {
    const userAgent = navigator.userAgent;

    // iOS
    const iosMatch = userAgent.match(/OS (\d+)_(\d+)(_\d+)?/);
    if (iosMatch) {
      return {
        name: 'iOS',
        version: iosMatch[1],
        fullVersion: `iOS ${iosMatch[1]}.${iosMatch[2]}`,
      };
    }

    // Android
    const androidMatch = userAgent.match(/Android (\d+)\.?(\d+)?/);
    if (androidMatch) {
      return {
        name: 'Android',
        version: androidMatch[1],
        fullVersion: `Android ${androidMatch[1]}${androidMatch[2] ? '.' + androidMatch[2] : ''}`,
      };
    }

    // macOS
    const macMatch = userAgent.match(/Mac OS X (\d+)[_.](\d+)/);
    if (macMatch) {
      return {
        name: 'macOS',
        version: macMatch[1],
        fullVersion: `macOS ${macMatch[1]}.${macMatch[2]}`,
      };
    }

    // Windows
    const winMatch = userAgent.match(/Windows NT (\d+\.\d+)/);
    if (winMatch) {
      const ntVersion = winMatch[1];
      const version = this.ntVersionToWindowsVersion(ntVersion);
      return {
        name: 'Windows',
        version: version,
        fullVersion: `Windows ${version}`,
      };
    }

    return { name: 'Unknown', version: 'Unknown', fullVersion: 'Unknown' };
  }

  private ntVersionToWindowsVersion(ntVersion: string): string {
    const mapping: Record<string, string> = {
      '10.0': '10/11',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
      '6.0': 'Vista',
    };
    return mapping[ntVersion] || ntVersion;
  }

  /**
   * Get device model name
   */
  private getDeviceModel(): string {
    const userAgent = navigator.userAgent;

    if (/iPhone/.test(userAgent)) {
      return 'iPhone';
    }

    if (/iPad/.test(userAgent)) {
      return 'iPad';
    }

    // Android devices - try to extract model
    const androidMatch = userAgent.match(/;\s*([^;)]+)\s*Build/);
    if (androidMatch) {
      return androidMatch[1].trim();
    }

    if (/Macintosh/.test(userAgent)) {
      return 'Mac';
    }

    // Generic browser
    return `${this.getBrowserName()} Browser`;
  }

  /**
   * Get browser name
   */
  private getBrowserName(): string {
    const userAgent = navigator.userAgent;

    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      return 'Chrome';
    }
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      return 'Safari';
    }
    if (userAgent.includes('Firefox')) {
      return 'Firefox';
    }
    if (userAgent.includes('Edg')) {
      return 'Edge';
    }

    return 'Unknown';
  }

  /**
   * Get device manufacturer
   */
  private getManufacturer(): string {
    const userAgent = navigator.userAgent.toLowerCase();

    if (/iphone|ipad|ipod|macintosh/.test(userAgent)) {
      return 'Apple';
    }
    if (/samsung/.test(userAgent)) {
      return 'Samsung';
    }
    if (/pixel/.test(userAgent)) {
      return 'Google';
    }
    if (/huawei/.test(userAgent)) {
      return 'Huawei';
    }
    if (/xiaomi/.test(userAgent)) {
      return 'Xiaomi';
    }
    if (/windows/.test(userAgent)) {
      return 'Microsoft';
    }

    return 'Unknown';
  }

  /**
   * Get application version
   */
  private getAppVersion(): string {
    return '1.0.0-web';
  }
}

